import { useNavigator } from '@repo/toolkit/use-navigator'
import { SettingIcon } from '@repo/ui/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { getMedicalTerm } from '../../../api/medical-terms/get-medical-term'
import { updateMedicalTerm } from '../../../api/medical-terms/update-medical-term'
import { ItemNotFound } from '../../../components/elements/item-not-found'
import LoadingScreen from '../../../components/elements/loading/loading-screen'
import UserNavBar from '../../../components/elements/nav-bar'
import MedicalTermForm from '../../../components/pages/medical-term/medical-term-form'
import { Paths } from '../../../lib/constants/path'
import { updateButtonsConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import NotFoundPage from '../../common/not-found'
import { mapMedicalTermIntoInput } from './utils'

const MedicalTermUpdatePage = () => {
  const { id } = useParams<{ id: string }>()
  const { navigate, back } = useNavigator()
  const queryClient = useQueryClient()

  if (!id || isNaN(+id)) return <ItemNotFound title="Identifiant de terme médical invalide" />

  const { mutateAsync } = useMutation({
    mutationFn: updateMedicalTerm,
    onError: showAxiosError,
    onSuccess() {
      queryClient.invalidateQueries({ exact: true, queryKey: ['medical-terms', id] })
      toast.success('Terme médical modifié avec succès')
    }
  })

  const { data, isLoading } = useQuery({
    queryKey: ['medical-terms', id],
    queryFn: () => getMedicalTerm(+id)
  })

  if (isLoading) return <LoadingScreen />
  if (!data) return <NotFoundPage />

  return (
    <div>
      <UserNavBar title={`Modifier Termes médicaux ${data.label}`} icon={SettingIcon} />
      <div className={'h-4'} />
      <MedicalTermForm
        defaultValues={mapMedicalTermIntoInput(data)}
        buttonsConfig={updateButtonsConfig({
          onCancel: () => back(Paths.MEDICAL_TERM_LIST_PAGE),
          onUpdate: (values) => mutateAsync({ id: data.id, ...values }),
          onUpdateThenCreate: async (values) => {
            await mutateAsync({ id: data.id, ...values })
            navigate(Paths.MEDICAL_TERM_CREATE_PAGE)
          },
          onUpdateThenList: async (values) => {
            await mutateAsync({ id: data.id, ...values })
            navigate(Paths.MEDICAL_TERM_LIST_PAGE, { replace: true })
          }
        })}
      />
    </div>
  )
}
export default MedicalTermUpdatePage
